/**
 * Regras do botão "Instalar app" que não dependem do navegador em execução (por isso ficam fora
 * do hook e podem ser testadas em Node puro: ver instalacaoPwa.test.mjs).
 *
 * Em Chrome, Edge e Android o navegador manda um aviso de instalação e o Elos abre a janela de
 * instalação com um toque. No iPhone nenhum navegador manda esse aviso, e em outros navegadores o
 * aviso nem sempre chega. Nesses casos o botão continua visível e abre um passo a passo.
 */

export type AmbienteInstalacao = "ios-safari" | "ios-chrome" | "ios-outro" | "outro";

export interface InstrucoesInstalacao {
  intro: string;
  passos: string[];
  /** Saída para quem não encontra a opção no navegador atual. */
  dica?: string;
}

/**
 * Descobre em qual navegador a pessoa está, só para escolher o texto do passo a passo.
 * iPad moderno se apresenta como Mac (`MacIntel`), mas tem tela de toque.
 */
export function detectarAmbiente(
  userAgent: string,
  plataforma = "",
  pontosDeToque = 0,
): AmbienteInstalacao {
  const ehIosPeloUa = /iphone|ipad|ipod/i.test(userAgent);
  const ehIpadComoMac = plataforma === "MacIntel" && pontosDeToque > 1;
  if (!ehIosPeloUa && !ehIpadComoMac) return "outro";
  if (/crios/i.test(userAgent)) return "ios-chrome";
  if (/fxios|edgios|opios|opt\//i.test(userAgent)) return "ios-outro";
  return "ios-safari";
}

export function instrucoesDeInstalacao(ambiente: AmbienteInstalacao): InstrucoesInstalacao {
  switch (ambiente) {
    case "ios-safari":
      return {
        intro: "No iPhone, o Safari não deixa o Elos abrir a instalação sozinho. São dois toques:",
        passos: [
          "Toque em Compartilhar, o quadrado com a seta, na barra do Safari.",
          "Escolha “Adicionar à Tela de Início”.",
        ],
      };
    case "ios-chrome":
      return {
        intro: "No iPhone, o Chrome não deixa o Elos abrir a instalação sozinho. São dois toques:",
        passos: [
          "Toque em Compartilhar, o quadrado com a seta, na barra de endereço do Chrome ou no menu ⋯.",
          "Escolha “Adicionar à Tela de Início”.",
        ],
        dica: "Se a opção não aparecer, abra o Elos no Safari e repita os dois toques.",
      };
    case "ios-outro":
      return {
        intro:
          "No iPhone, este navegador não deixa o Elos abrir a instalação sozinho. São dois toques:",
        passos: [
          "Toque em Compartilhar, o quadrado com a seta, na barra do navegador.",
          "Escolha “Adicionar à Tela de Início”.",
        ],
        dica: "Se a opção não aparecer, abra o Elos no Safari e repita os dois toques.",
      };
    default:
      return {
        intro: "Este navegador não deixa o Elos abrir a instalação sozinho. São dois toques:",
        passos: [
          "Abra o menu do navegador (⋮ ou ⋯).",
          "Escolha “Instalar app” ou “Adicionar à tela inicial”.",
        ],
        dica: "Se a opção não aparecer, abra o Elos no Chrome ou no Edge.",
      };
  }
}

export const TEXTO_JA_INSTALOU =
  "Já instalou? Abra o Elos pelo ícone na tela inicial. Aí este botão não aparece mais.";
